/**
 * Texturas.ts — Texturas geradas em canvas 2D (Fase 6, o "grafico melhor").
 *
 * Tudo que o jogo desenha vem daqui: planeta com relevo e sombra, aura de
 * territorio, nave em forma de dardo com brilho, rastro, estrela, nebulosa e
 * particula. Nada de imagem externa: gera na hora, uma vez, e fica em cache.
 * Trocar GlowFilter por brilho "assado" na textura e o que deixa 500 naves a
 * 60 fps (cada Sprite entra no batch do Pixi; cada filtro e um passe a mais).
 */

import { Texture } from 'pixi.js';

const cache = new Map<string, Texture>();

function canvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('canvas 2d indisponivel');
  return [c, ctx];
}

function lembrar(chave: string, gerar: () => HTMLCanvasElement): Texture {
  const t = cache.get(chave);
  if (t) return t;
  const tex = Texture.from(gerar());
  cache.set(chave, tex);
  return tex;
}

/** Gerador pseudoaleatorio deterministico (mulberry32): mesmo planeta, mesma cara. */
function rng(seed: number): () => number {
  let a = (seed >>> 0) || 1;
  return () => {
    a += 0x6d2b79f5;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hexCss(cor: number): string {
  return '#' + cor.toString(16).padStart(6, '0');
}

// ---------------------------------------------------------------------------
// Aura de territorio: disco radial branco -> transparente (tinta pela cor do dono)
// ---------------------------------------------------------------------------
export function texturaAura(): Texture {
  return lembrar('aura', () => {
    const S = 256;
    const [c, ctx] = canvas(S, S);
    const g = ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
    g.addColorStop(0, 'rgba(255,255,255,0.9)');
    g.addColorStop(0.35, 'rgba(255,255,255,0.35)');
    g.addColorStop(0.7, 'rgba(255,255,255,0.08)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, S, S);
    return c;
  });
}

// ---------------------------------------------------------------------------
// Planeta: esfera sombreada com crateras ou faixas, unica por (id, tamanho)
// ---------------------------------------------------------------------------
const PALETA_PLANETA = [
  [215, 0.28, 0.20], // azul-aco
  [255, 0.30, 0.19], // violeta
  [185, 0.28, 0.18], // petroleo
  [20,  0.32, 0.20], // ferrugem
  [30,  0.22, 0.24], // areia escura
  [150, 0.20, 0.17], // musgo
  [330, 0.22, 0.19], // vinho
  [200, 0.10, 0.22], // cinza-azulado
];

export function texturaPlaneta(id: number, raio: number): Texture {
  return lembrar(`planeta:${id}:${raio}`, () => {
    const S = Math.max(128, Math.round(raio * 2 * 3)); // 3x o diametro: nitido no zoom
    const r = S / 2;
    const [c, ctx] = canvas(S, S);
    const rand = rng(id * 7919 + 17);
    const [h, sat, lum] = PALETA_PLANETA[Math.floor(rand() * PALETA_PLANETA.length)];
    const base = `hsl(${h} ${Math.round(sat * 100)}% ${Math.round(lum * 100)}%)`;
    const claro = `hsl(${h} ${Math.round(sat * 100)}% ${Math.round(lum * 100 + 22)}%)`;
    const escuro = `hsl(${h} ${Math.round(sat * 100)}% ${Math.round(Math.max(4, lum * 100 - 12))}%)`;

    ctx.save();
    ctx.beginPath();
    ctx.arc(r, r, r - 1, 0, Math.PI * 2);
    ctx.clip();

    // Corpo: luz vindo de cima-esquerda
    const luz = ctx.createRadialGradient(r * 0.62, r * 0.58, r * 0.05, r, r, r);
    luz.addColorStop(0, claro);
    luz.addColorStop(0.55, base);
    luz.addColorStop(1, escuro);
    ctx.fillStyle = luz;
    ctx.fillRect(0, 0, S, S);

    const gasoso = rand() < 0.35;
    if (gasoso) {
      // Faixas horizontais suaves (gigante gasoso)
      const nFaixas = 6 + Math.floor(rand() * 6);
      for (let i = 0; i < nFaixas; i++) {
        const y = rand() * S;
        const alt = S * (0.03 + rand() * 0.07);
        ctx.fillStyle = `hsla(${h + (rand() - 0.5) * 30} ${Math.round(sat * 100)}% ${Math.round(lum * 100 + (rand() - 0.4) * 30)}% / ${0.18 + rand() * 0.2})`;
        ctx.beginPath();
        ctx.ellipse(r, y, r * 1.15, alt, 0, 0, Math.PI * 2);
        ctx.fill();
      }
    } else {
      // Crateras: disco escuro + borda clara do lado da luz
      const n = 5 + Math.floor(rand() * 9);
      for (let i = 0; i < n; i++) {
        const ang = rand() * Math.PI * 2;
        const d = rand() * r * 0.78;
        const cx = r + Math.cos(ang) * d;
        const cy = r + Math.sin(ang) * d;
        const cr = r * (0.05 + rand() * 0.13);
        ctx.fillStyle = `rgba(0,0,0,${0.16 + rand() * 0.2})`;
        ctx.beginPath();
        ctx.arc(cx, cy, cr, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = `rgba(255,255,255,${0.10 + rand() * 0.12})`;
        ctx.lineWidth = Math.max(1, cr * 0.18);
        ctx.beginPath();
        ctx.arc(cx, cy, cr, Math.PI * 0.9, Math.PI * 1.9);
        ctx.stroke();
      }
      // Manchas claras (continentes/gelo)
      const m = 2 + Math.floor(rand() * 3);
      for (let i = 0; i < m; i++) {
        ctx.fillStyle = `rgba(255,255,255,${0.05 + rand() * 0.06})`;
        ctx.beginPath();
        ctx.ellipse(r + (rand() - 0.5) * r, r + (rand() - 0.5) * r, r * (0.2 + rand() * 0.3), r * (0.1 + rand() * 0.2), rand() * Math.PI, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Terminador: sombra do lado direito-inferior
    const sombra = ctx.createRadialGradient(r * 0.7, r * 0.7, r * 0.55, r, r, r * 1.02);
    sombra.addColorStop(0, 'rgba(0,0,0,0)');
    sombra.addColorStop(1, 'rgba(0,0,0,0.78)');
    ctx.fillStyle = sombra;
    ctx.fillRect(0, 0, S, S);

    // Brilho especular
    const spec = ctx.createRadialGradient(r * 0.6, r * 0.55, 0, r * 0.6, r * 0.55, r * 0.5);
    spec.addColorStop(0, 'rgba(255,255,255,0.22)');
    spec.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = spec;
    ctx.fillRect(0, 0, S, S);
    ctx.restore();

    // Borda atmosferica fina (independente do dono: o dono pinta o aro por fora)
    ctx.strokeStyle = 'rgba(255,255,255,0.10)';
    ctx.lineWidth = Math.max(1, S * 0.008);
    ctx.beginPath();
    ctx.arc(r, r, r - 1.5, 0, Math.PI * 2);
    ctx.stroke();
    return c;
  });
}

// ---------------------------------------------------------------------------
// Nave: dardo apontando para +x, brilho assado na cor
// ---------------------------------------------------------------------------
export function texturaNave(cor: number): Texture {
  return lembrar(`nave:${cor}`, () => {
    const W = 40, H = 24;
    const [c, ctx] = canvas(W, H);
    const css = hexCss(cor);
    const cx = W / 2 + 2, cy = H / 2;
    const dardo = () => {
      ctx.beginPath();
      ctx.moveTo(cx + 11, cy);
      ctx.lineTo(cx - 7, cy + 5.5);
      ctx.lineTo(cx - 3.5, cy);
      ctx.lineTo(cx - 7, cy - 5.5);
      ctx.closePath();
    };
    // Halo (2 passes de shadowBlur)
    ctx.shadowColor = css;
    ctx.shadowBlur = 9;
    ctx.fillStyle = css;
    dardo(); ctx.fill();
    ctx.shadowBlur = 4;
    dardo(); ctx.fill();
    ctx.shadowBlur = 0;
    // Nucleo claro
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.beginPath();
    ctx.moveTo(cx + 7, cy);
    ctx.lineTo(cx - 3, cy + 2.2);
    ctx.lineTo(cx - 1.5, cy);
    ctx.lineTo(cx - 3, cy - 2.2);
    ctx.closePath();
    ctx.fill();
    return c;
  });
}

/** Rastro: faixa que some para a esquerda; ancora (1, 0.5) no nariz da nave. */
export function texturaRastro(cor: number): Texture {
  return lembrar(`rastro:${cor}`, () => {
    const W = 72, H = 10;
    const [c, ctx] = canvas(W, H);
    const css = hexCss(cor);
    const g = ctx.createLinearGradient(0, 0, W, 0);
    g.addColorStop(0, 'rgba(0,0,0,0)');
    g.addColorStop(0.6, css.replace('#', 'rgba(') === css ? css : css); // placeholder (sobrescrito abaixo)
    const [rr, gg, bb] = [(cor >> 16) & 255, (cor >> 8) & 255, cor & 255];
    const g2 = ctx.createLinearGradient(0, 0, W, 0);
    g2.addColorStop(0, `rgba(${rr},${gg},${bb},0)`);
    g2.addColorStop(0.7, `rgba(${rr},${gg},${bb},0.55)`);
    g2.addColorStop(1, `rgba(255,255,255,0.75)`);
    ctx.fillStyle = g2;
    ctx.beginPath();
    ctx.moveTo(0, H / 2);
    ctx.lineTo(W, 0.5);
    ctx.lineTo(W, H - 0.5);
    ctx.closePath();
    ctx.fill();
    return c;
  });
}

/** Estrela/particula: ponto suave. */
export function texturaPonto(): Texture {
  return lembrar('ponto', () => {
    const S = 16;
    const [c, ctx] = canvas(S, S);
    const g = ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.4, 'rgba(255,255,255,0.55)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, S, S);
    return c;
  });
}

/** Nebulosa: 3 manchas suaves sobrepostas, cor por matiz (h). Alpha baixo ja no pixel. */
export function texturaNebulosa(h: number, seed: number): Texture {
  return lembrar(`nebulosa:${h}:${seed}`, () => {
    const S = 512;
    const [c, ctx] = canvas(S, S);
    const rand = rng(seed);
    for (let i = 0; i < 4; i++) {
      const cx = S * (0.3 + rand() * 0.4);
      const cy = S * (0.3 + rand() * 0.4);
      const rad = S * (0.22 + rand() * 0.25);
      const hue = h + (rand() - 0.5) * 40;
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, rad);
      g.addColorStop(0, `hsla(${hue} 70% 55% / ${0.22 + rand() * 0.12})`);
      g.addColorStop(0.5, `hsla(${hue} 70% 45% / ${0.08})`);
      g.addColorStop(1, `hsla(${hue} 70% 40% / 0)`);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, S, S);
    }
    return c;
  });
}
